import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, map, finalize } from 'rxjs';
import { ApiConstants } from 'src/app/base/api-constants';
import { AppGlobalService } from 'src/app/services/app.global.service';
import { HttpBaseService } from 'src/app/base/http-base.service';
import {
  GetComponentChangesParams,
  ComponentChange,
  ComponentChangeDetail,
  ComponentChangeDetailsResponse
} from './view-changes.types';

interface ComponentChangesApiResponse {
  appNameMap?: Record<string, string>;
  data: ComponentChange[];
  total: number;
}

// Raw API response item structure
interface RawComponentChangeItem {
  componentId: string;
  componentType: string;
  appId?: string;
  branchId: string;
  // Updated
  updatedUserId?: string;
  updatedUserName?: string;
  updatedOn?: number;
  // Created
  creatorId?: string;
  creatorName?: string;
  createdOn?: number | string;
  // Deleted
  deleteUserId?: string;
  deleteUserName?: string;
  deletedOn?: number;
  // Renamed
  renamedUserId?: string;
  renamedUserName?: string;
  renamedOn?: number;
}

interface RawComponentChangesApiResponse {
  results: RawComponentChangeItem[];
  page?: number;
  pageSize?: number;
  total: number;
  filtered?: number;
  appNameMap?: Record<string, string>;
}

// Extended response that includes filter options extracted from results
export interface ComponentChangesWithFiltersResponse {
  appNameMap?: Record<string, string>;
  data: ComponentChange[];
  total: number;
  filterOptions: {
    componentTypes: string[];
  };
}

@Injectable({ providedIn: 'root' })
export class ViewChangesService {
  private readonly http = inject(HttpClient);
  private readonly appGlobalService = inject(AppGlobalService);
  private readonly baseService = inject(HttpBaseService);

  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      'x-rappit-application': this.appGlobalService.get('appId') as string,
      'x-rappit-organization': this.appGlobalService.get('orgId') as string
    });
  }

  private getBranchHeaders(branchId: string): HttpHeaders {
    return new HttpHeaders({
      'x-rappit-application': branchId,
      'x-rappit-organization': this.appGlobalService.get('orgId') as string
    });
  }

  // Transform raw API response item to ComponentChange format
  private mapRawItemToComponentChange(item: RawComponentChangeItem): ComponentChange {
    // Modified by - from updatedUserId/updatedUserName/updatedOn
    const lastModified = {
      author: item.updatedUserName || item.updatedUserId || '',
      timestamp: item.updatedOn ? new Date(item.updatedOn).toISOString() : ''
    };

    // Created - from creatorId/creatorName/createdOn
    let createdTimestamp = '';
    if (item.createdOn) {
      createdTimestamp = typeof item.createdOn === 'string' ? item.createdOn : new Date(item.createdOn).toISOString();
    }
    const created = {
      author: item.creatorName || item.creatorId || '',
      timestamp: createdTimestamp
    };

    // Deleted - from deleteUserId/deleteUserName/deletedOn
    const deleteAuthor = item.deleteUserName || item.deleteUserId || '';
    const deleted = deleteAuthor
      ? { author: deleteAuthor, timestamp: item.deletedOn ? new Date(item.deletedOn).toISOString() : '' }
      : undefined;

    // Renamed - from renamedUserId/renamedUserName/renamedOn
    const renameAuthor = item.renamedUserName || item.renamedUserId || '';
    const renamed = renameAuthor
      ? { author: renameAuthor, timestamp: item.renamedOn ? new Date(item.renamedOn).toISOString() : '' }
      : undefined;

    return {
      appId: item.appId,
      applicationName: '', // Will be enriched later based on appId and appNameMap
      componentId: item.componentId || '',
      componentType: item.componentType || '',
      componentName: item.componentId || '',
      updatedUserId: item.updatedUserId || '',
      creatorId: item.creatorId || '',
      deleteUserId: item.deleteUserId || '',
      renamedUserId: item.renamedUserId || '',
      updated: lastModified,
      created,
      deleted,
      renamed
    };
  }

  getComponentChanges(params: GetComponentChangesParams): Observable<ComponentChangesApiResponse> {
    const { branchId, filters, sort, page, length, suiteMode } = params;

    // Build payload with required branchId and pagination
    const payload: Record<string, unknown> = {
      branchId: branchId,
      start: (page - 1) * length,
      pageSize: length
    };

    //Send suiteMode flag
    if (suiteMode) {
      payload['suiteMode'] = true;
    }

    //Application filter — sends appIds to backend
    if (filters.appIdFilter && filters.appIdFilter.length > 0) {
      payload['appIdFilter'] = filters.appIdFilter;
    }

    // Add sort only if provided
    if (sort) {
      payload['sort'] = sort;
    }

    // Add timeFilter only if date filters are provided - send null for empty dates
    if (filters.dateFrom || filters.dateTo) {
      payload['timeFilter'] = {
        sinceTimestamp: filters.dateFrom ? new Date(filters.dateFrom).getTime() : null,
        toTimestamp: filters.dateTo ? new Date(filters.dateTo).getTime() : null
      };
    }

    // Add componentType filter only if provided and has non-empty values
    const validComponentTypes = (filters.componentType || []).filter((v) => v && v.trim() !== '');
    if (validComponentTypes.length > 0) {
      payload['componentType'] = validComponentTypes;
    }

    console.warn('API Payload:', JSON.stringify(payload));

    return this.http.post<RawComponentChangesApiResponse | RawComponentChangeItem[]>(ApiConstants.getComponentChanges.url, payload, { headers: this.getBranchHeaders(branchId) }).pipe(
      map((response) => {
        const rawItems: RawComponentChangeItem[] = Array.isArray(response) 
          ? response 
          : (response.results || []);
        const totalCount = Array.isArray(response) ? response.length : (response.total || rawItems.length);
        const appNameMap = Array.isArray(response) ? undefined : response.appNameMap;
        return {
          appNameMap,
          data: rawItems.map((item) => this.mapRawItemToComponentChange(item)),
          total: totalCount
        };
      })
    );
  }

  // Get component changes along with filter options in a single API call
  getComponentChangesWithFilters(params: GetComponentChangesParams): Observable<ComponentChangesWithFiltersResponse> {
    const { branchId, filters, sort, page, length, suiteMode } = params;

    // Build payload with required branchId and pagination
    const payload: Record<string, unknown> = {
      branchId: branchId,
      start: (page - 1) * length,
      pageSize: length
    };

    if (suiteMode) {
      payload['suiteMode'] = true;
    }

    if (filters.appIdFilter && filters.appIdFilter.length > 0) {
      payload['appIdFilter'] = filters.appIdFilter.filter((v) => v && v.trim() !== '');
    }

    // Add sort only if provided
    if (sort) {
      payload['sort'] = sort;
    }

    // Add timeFilter only if date filters are provided - send null for empty dates
    if (filters.dateFrom || filters.dateTo) {
      payload['timeFilter'] = {
        sinceTimestamp: filters.dateFrom ? new Date(filters.dateFrom).getTime() : null,
        toTimestamp: filters.dateTo ? new Date(filters.dateTo).getTime() : null
      };
    }

    // Add componentType filter only if provided and has non-empty values
    const validComponentTypes = (filters.componentType || []).filter((v) => v && v.trim() !== '');
    if (validComponentTypes.length > 0) {
      payload['componentType'] = validComponentTypes;
    }

    console.warn('API Payload (with filters):', JSON.stringify(payload));

    return this.http.post<RawComponentChangesApiResponse | RawComponentChangeItem[]>(ApiConstants.getComponentChanges.url, payload, { headers: this.getBranchHeaders(branchId) }).pipe(
      map((response) => {    
        const rawItems: RawComponentChangeItem[] = Array.isArray(response) 
          ? response 
          : (response.results || []);
        const totalCount = Array.isArray(response) ? response.length : (response.total || rawItems.length);
        const appNameMap = Array.isArray(response) ? undefined : response.appNameMap;
        const mappedData = rawItems.map((item) => this.mapRawItemToComponentChange(item));

        const componentTypes = [...new Set(
          mappedData
            .map((item) => item.componentType)
            .filter((val): val is string => val !== null && val !== undefined && val !== '')
        )];

        return {
          appNameMap,
          data: mappedData,
          total: totalCount,
          filterOptions: {
            componentTypes
          }
        };
      })
    );
  }

  getComponentChangeDetails(componentId: string, branchId: string, componentType: string): Observable<ComponentChangeDetailsResponse> {
    const payload = {
      componentId,
      branchId,
      componentType
    };

    // Raw API response item structure
    interface RawComponentDetailItem {
      sid?: string;
      createdBy?: string;
      createdDate?: number;
      modifiedBy?: string;
      modifiedDate?: number;
      action?: string;
      componentId?: string;
      componentType?: string;
      mergeLog?: boolean;
      oldValue?: string;
      newValue?: string;
    }

    return this.http.post<RawComponentDetailItem[] | { data: RawComponentDetailItem[]; total: number }>(
      ApiConstants.getComponentChangeDetails.url,
      payload,
      { headers: this.getBranchHeaders(branchId) }
    ).pipe(
      map((response) => {
        // Handle both array response and object with data property
        const rawItems: RawComponentDetailItem[] = Array.isArray(response) ? response : response.data || [];

        // Transform raw items to ComponentChangeDetail format
        const mappedData: ComponentChangeDetail[] = rawItems.map((item) => {
          let dateTime = '';
          if (item.modifiedDate) {
            dateTime = new Date(item.modifiedDate).toISOString();
          } else if (item.createdDate) {
            dateTime = new Date(item.createdDate).toISOString();
          }
          return {
            dateTime,
            action: item.action || '',
            author: item.modifiedBy || item.createdBy || '',
            mergeLog: item.mergeLog || false,
            oldValue: item.oldValue,
            newValue: item.newValue
          };
        });

        return {
          data: mappedData,
          total: mappedData.length
        };
      })
    );
  }

  /**
   * Fetch app meta info for a specific branch
   * @param branchId The branch ID to fetch meta info for
   * @returns Observable with the meta info response
   */
  getBranchMetaInfo(branchId: string): Observable<any> {
    const paginationRequest = {
      start: 0,
      length: 10000 // Fetch all records
    };

    // Temporarily set branchId as appId for the API call (required for withAppIdInUrl)
    const originalAppId = this.appGlobalService.get('appId');
    this.appGlobalService.write('appId', branchId);

    return (this.baseService.post(ApiConstants.getAppMetaInfo, paginationRequest) as Observable<any>).pipe(
      finalize(() => {
        // Restore original appId after response or error
        this.appGlobalService.write('appId', originalAppId);
      })
    );
  }
}
